import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BookingModal from './BookingModal';

const COLORS = {
  primary: "#0760ad",
  primaryLight: "#098a48",
  secondary: "#118a94",
  white: "#FFFFFF",
  accent: "#FF6B35",
  lightBg: "#F8FAFC"
};

const PackageGrid = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL 

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API_URL}/packages/`);
        setPackages(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError('Failed to load packages. Please try again later.');
        setLoading(false);
        toast.error('Failed to load packages');
      }
    };

    fetchPackages();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const handleBookNow = (pkg) => {
    setSelectedPackage(pkg);
    setShowBookingModal(true);
  };

  const handleBookingSuccess = (bookingData) => {
    // You can add the booking to your state if needed
    console.log('Booking successful:', bookingData);
  };
  
  if (error) {
    return (
      <div className="py-20 px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: COLORS.lightBg }}>
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-extrabold mb-4 text-gray-900">
            Comprehensive <span className="text-[#0760ad]">Health</span> Packages
          </h2>
          <div className="w-24 h-1 bg-[#0760ad] mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tailored medical checkup packages designed for your specific needs at competitive prices
          </p>
        </motion.div>

        {/* Package Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              whileHover={{ y: -5 }}
              transition={{ 
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: index * 0.1
              }}
              viewport={{ once: true, margin: "-100px" }}
              className="relative group"
            >
              {/* Ribbon for featured package */}
              {pkg.is_featured && (
                <div className="absolute -top-2 -right-2 bg-[#FF6B35] text-white px-3 py-1 text-sm font-bold rounded z-10 shadow-md">
                  Featured
                </div>
              )}

              <div className="h-full bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 group-hover:shadow-xl">
                {/* Color header */}
                <div 
                  className="h-2 w-full" 
                  style={{ backgroundColor: pkg.color_code || COLORS.primary }}
                ></div>

                <div className="p-6 flex flex-col justify-between h-full">
                  {/* Package Title */}
                  <div className='flex'>
                  <h3 
                    className="text-2xl font-bold mb-3" 
                    style={{ color: pkg.color_code || COLORS.primary }}
                  >
                    {pkg.title}
                  </h3>

                  <div>

                  {/* Price with animated background */}
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="inline-block mb-6"
                  >
                    <div 
                      className="px-4 py-2 rounded-lg font-bold text-white"
                      style={{ 
                        backgroundColor: pkg.color_code || COLORS.primary,
                        boxShadow: `0 4px 14px ${pkg.color_code || COLORS.primary}40`
                      }}
                    >
                      {pkg.price} Taka
                    </div>
                  </motion.div>

                  </div>
                  </div>
                  {/* Features List */}
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <motion.li 
                        key={i}
                        whileHover={{ x: 5 }}
                        className="flex items-start"
                      >
                        <svg 
                          className="flex-shrink-0 h-5 w-5 mt-0.5 mr-2" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke={COLORS.primaryLight}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{feature.description}</span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <motion.button
        whileHover={{ 
          scale: 1.03,
          boxShadow: `0 5px 15px ${pkg.color_code || COLORS.primary}80`
        }}
        whileTap={{ scale: 0.98 }}
        className="w-full py-3 rounded-lg font-semibold text-white transition-all duration-300"
        style={{ 
          backgroundColor: pkg.color_code || COLORS.primary,
        }}
        onClick={() => handleBookNow(pkg)}
      >
        Book Now
        <span className="ml-2">→</span>
      </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>


      </div>
      {showBookingModal && selectedPackage && (
        <BookingModal
          packageItem={selectedPackage}
          onClose={() => setShowBookingModal(false)}
          onBookingSuccess={handleBookingSuccess}
        />
      )}
    </div>
  );
};

export default PackageGrid;